import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManualOrderForm = ({ onClose, onSuccess }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  
  // Form state
  const [customerData, setCustomerData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  
  const [orderItems, setOrderItems] = useState([]);
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  });
  
  const [orderNotes, setOrderNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [depositAmount, setDepositAmount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('pending');

  useEffect(() => {
    loadProducts();
    loadCustomers();
  }, []);

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const handleCustomerSearch = (term) => {
    setSearchTerm(term);
    if (term.length > 2) {
      const found = customers.find(c => 
        c.email.toLowerCase().includes(term.toLowerCase()) ||
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(term.toLowerCase())
      );
      if (found) {
        setSelectedCustomer(found);
        setCustomerData({
          email: found.email,
          first_name: found.first_name,
          last_name: found.last_name,
          phone: found.phone || ''
        });
        setIsNewCustomer(false);
      } else {
        setIsNewCustomer(true);
        setSelectedCustomer(null);
      }
    }
  };

  const addOrderItem = () => {
    setOrderItems([...orderItems, {
      product_id: '',
      quantity: 1,
      price: 0,
      product_name: '',
      customization: {},
      customization_notes: ''
    }]);
  };

  const updateOrderItem = (index, field, value) => {
    const updated = [...orderItems];
    updated[index][field] = value;
    
    // Auto-fill price when product is selected
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        updated[index].price = product.price;
        updated[index].product_name = product.name;
      }
    }
    
    setOrderItems(updated);
  };

  const removeOrderItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateShipping = () => {
    const rates = {
      'standard': 5.99,
      'express': 15.99,
      'overnight': 29.99,
      'pickup': 0
    };
    return rates[shippingMethod] || 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() + calculateShipping();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (orderItems.length === 0) {
      alert('Please add at least one product to the order');
      return;
    }
    
    if (!customerData.email || !customerData.first_name || !customerData.last_name) {
      alert('Please fill in all customer information');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      const total = calculateTotal();
      const balanceOwed = total - (depositAmount || 0);
      
      const orderData = {
        customer_info: customerData,
        is_new_customer: isNewCustomer,
        existing_customer_id: selectedCustomer?.id,
        items: orderItems,
        shipping_address: shippingAddress,
        payment_method: paymentMethod,
        shipping_method: shippingMethod,
        notes: orderNotes,
        subtotal: calculateSubtotal(),
        tax: calculateTax(),
        shipping_cost: calculateShipping(),
        total: total,
        deposit_amount: depositAmount || 0,
        balance_owed: balanceOwed,
        payment_status: depositAmount > 0 ? (balanceOwed > 0 ? 'partially_paid' : 'paid') : 'pending',
        manual_order: true
      };
      
      const response = await axios.post(`${API}/admin/manual-order`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Order created successfully! Customer will receive an invitation email.');
      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error('Failed to create order:', error);
      alert('Failed to create order: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`${theme.card} rounded-lg shadow-xl max-w-4xl w-full my-8`}>
        <div className={`p-6 border-b ${theme.border}`}>
          <div className="flex justify-between items-center">
            <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>📝 Create Manual Order</h2>
            <button onClick={onClose} className={`text-2xl ${theme.textSecondary} hover:${theme.textPrimary}`}>×</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Customer Search/Selection */}
          <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
            <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>👤 Customer Information</h3>
            
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                Search Existing Customer (or enter new)
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                placeholder="Search by email or name..."
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
              />
              {isNewCustomer && searchTerm.length > 2 && (
                <p className="text-sm text-green-600 mt-1">✓ New customer - invitation will be sent</p>
              )}
              {selectedCustomer && (
                <p className="text-sm text-blue-600 mt-1">✓ Existing customer: {selectedCustomer.email}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Email *</label>
                <input
                  type="email"
                  required
                  value={customerData.email}
                  onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Phone</label>
                <input
                  type="tel"
                  value={customerData.phone}
                  onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>First Name *</label>
                <input
                  type="text"
                  required
                  value={customerData.first_name}
                  onChange={(e) => setCustomerData({...customerData, first_name: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Last Name *</label>
                <input
                  type="text"
                  required
                  value={customerData.last_name}
                  onChange={(e) => setCustomerData({...customerData, last_name: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
            <div className="flex justify-between items-center mb-3">
              <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>🛍️ Order Items</h3>
              <button
                type="button"
                onClick={addOrderItem}
                className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                + Add Item
              </button>
            </div>

            {orderItems.map((item, index) => (
              <div key={index} className={`grid grid-cols-12 gap-2 mb-3 p-3 rounded border ${theme.border}`}>
                <div className="col-span-5">
                  <select
                    value={item.product_id}
                    onChange={(e) => updateOrderItem(index, 'product_id', e.target.value)}
                    className={`w-full px-2 py-1 border ${theme.border} rounded ${theme.bgPrimary} ${theme.textPrimary} text-sm`}
                    required
                  >
                    <option value="">Select product...</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name} - ${p.price}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value))}
                    placeholder="Qty"
                    className={`w-full px-2 py-1 border ${theme.border} rounded ${theme.bgPrimary} ${theme.textPrimary} text-sm`}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    value={item.price}
                    onChange={(e) => updateOrderItem(index, 'price', parseFloat(e.target.value))}
                    placeholder="Price"
                    className={`w-full px-2 py-1 border ${theme.border} rounded ${theme.bgPrimary} ${theme.textPrimary} text-sm`}
                  />
                </div>
                <div className="col-span-2">
                  <div className={`px-2 py-1 ${theme.bgPrimary} rounded text-sm font-bold ${theme.textPrimary}`}>
                    ${(item.price * item.quantity).toFixed(2)}
                  </div>
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={() => removeOrderItem(index)}
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    ×
                  </button>
                </div>
                
                {/* Customization fields for this item */}
                <div className="col-span-12 mt-2 p-3 bg-purple-50 rounded border border-purple-200">
                  <p className="text-sm font-medium text-purple-800 mb-2">📝 Customization (Optional)</p>
                  <textarea
                    value={item.customization_notes || ''}
                    onChange={(e) => updateOrderItem(index, 'customization_notes', e.target.value)}
                    placeholder="Enter any customization notes, options, personalization details..."
                    rows={2}
                    className="w-full px-2 py-1 border border-purple-300 rounded text-sm"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Shipping Address */}
          <div className={`p-4 rounded-lg border ${theme.border} ${theme.bgSecondary}`}>
            <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>📦 Shipping Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Street Address</label>
                <input
                  type="text"
                  value={shippingAddress.street}
                  onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>City</label>
                <input
                  type="text"
                  value={shippingAddress.city}
                  onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>State</label>
                <input
                  type="text"
                  value={shippingAddress.state}
                  onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Postal Code</label>
                <input
                  type="text"
                  value={shippingAddress.postal_code}
                  onChange={(e) => setShippingAddress({...shippingAddress, postal_code: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Country</label>
                <input
                  type="text"
                  value={shippingAddress.country}
                  onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
            </div>
          </div>

          {/* Payment & Shipping Method */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
              >
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Shipping Method</label>
              <select
                value={shippingMethod}
                onChange={(e) => setShippingMethod(e.target.value)}
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
              >
                <option value="standard">Standard ($5.99)</option>
                <option value="express">Express ($15.99)</option>
                <option value="overnight">Overnight ($29.99)</option>
                <option value="pickup">Local Pickup (Free)</option>
              </select>
            </div>
          </div>

          {/* Order Notes */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Order Notes (Internal)</label>
            <textarea
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              rows="3"
              placeholder="Add notes about this order..."
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            />
          </div>

          {/* Order Summary */}
          <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${theme.isDark ? 'border-green-700' : 'border-green-200'}`}>
            <h3 className={`text-lg font-semibold mb-3 ${theme.textPrimary}`}>💰 Order Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={theme.textSecondary}>Subtotal:</span>
                <span className={`font-medium ${theme.textPrimary}`}>${calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className={theme.textSecondary}>Tax (8%):</span>
                <span className={`font-medium ${theme.textPrimary}`}>${calculateTax().toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className={theme.textSecondary}>Shipping:</span>
                <span className={`font-medium ${theme.textPrimary}`}>${calculateShipping().toFixed(2)}</span>
              </div>
              <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                <span className={`text-lg font-bold ${theme.textPrimary}`}>Total:</span>
                <span className="text-xl font-bold text-green-600">${calculateTotal().toFixed(2)}</span>
              </div>
              
              {/* Deposit Amount */}
              <div className={`pt-2 border-t ${theme.border}`}>
                <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Deposit Amount (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={calculateTotal()}
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
                {depositAmount > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className={theme.textSecondary}>Deposit:</span>
                      <span className="text-blue-600 font-medium">${depositAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className={theme.textSecondary}>Balance Remaining:</span>
                      <span className="text-orange-600 font-medium">${(calculateTotal() - depositAmount).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className={`p-6 border-t ${theme.border} flex justify-end gap-3`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Order & Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualOrderForm;
